import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnumAndPrefix } from 'components/final-form/radioUtils';
import FullScreenPageHeader from 'components/FullScreenPageHeader';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { PatientConsumerRenderProps, withPatientContext } from 'components/patient/PatientContext';
import UnableToWorkPdfModal from 'components/pdf-modal/UnableToWorkPdfModal';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import { FormState } from 'final-form';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Button, Grid, Icon, Popup, RadioProps } from 'semantic-ui-react';
import { getPdfUrl } from 'services/documentServices';
import {
  cancelPatientEmail,
  editPatientEmail,
  getPrescriptionDocumentFromPendingEmailsList,
  getReportDocumentFromPendingEmailsList,
  searchPatientEmails,
  sendPatientEmails
} from 'services/patientServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { PatientDocumentType, PatientEmailSendStatus } from 'ts-types/api.enums';
import {
  PatientEmailFormDto,
  PatientEmailRequest,
  PatientEmailSearchRequest,
  PatientPrescriptionEmailDto,
  PrescriptionDocumentRequest,
  SendPatientEmailsRequest,
  UnableToWorkEmailRequest
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';

const ContentDiv = styled.div`
  flex: 1 1 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledCount = styled.span`
  color: rgb(48 145 218);
`;

const StyledPraxSegment = styled(PraxStyledSegment)`
  margin-top: 0 !important;
  padding: 0 !important;
  margin-bottom: 5px !important;

  .radio-buttons {
    margin-top: 1rem;
    margin-left: 1rem;
    display: inline-flex;
    margin-right: 1.2rem;
  }

  .search-segment {
    margin-top: 1rem !important;
  }
`

const StyledTable = styled(StyledViewTableRow)`
  margin: 1rem !important;
`

const StyledButton = styled(Button)`
  padding: 2px !important;
  margin-bottom: 2px !important;
  background: transparent !important;
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const initialSearchValues: Partial<PatientEmailSearchRequest> = {
  sendStatus: PatientEmailSendStatus.REQUESTED,
  fromDate: moment().format("YYYY-MM-DD"),
  toDate: moment().format("YYYY-MM-DD")
}

const radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

interface Props extends AuthConsumerRenderProps,
    PatientConsumerRenderProps,
    MessageSourceProps,
    RouterProps,
    NotificationSystemProps {
}

interface PrescriptionPendingEmailsFieldProps extends FormState {
}

const PatientEmailsView = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  const {
    history, language, currentUser, options, selectionNotificationSystem,
    getMessage, getMessageWithNamedParams
  } = props;

  const [patientEmailId, setPatientEmailId] = useState(-1 as number);
  const [documentId, setDocumentId] = useState(-1 as number);
  const [treatmentId, setTreatmentId] = useState(-1 as number);
  const [patientEmails, setPatientEmails] = useState([] as PatientPrescriptionEmailDto[]);
  const [patientEmailsLoaded, setPatientEmailsLoaded] = useState(false);
  const [patientEmailPdfModalOpen, setPatientEmailPdfModalOpen] = useState(false);
  const [documentOutput, setDocumentOutput] = useState([] as Array<PatientEmailFormDto>);
  const [patientEmailsSaveInProgress, setPatientEmailsSaveInProgress] = useState(false);
  const [selectedPatientEmails, setSelectedPatientEmails] = useState(new Set() as Set<number>);
  const [searchValues, setSearchValues] = useState<Partial<PatientEmailSearchRequest>>({});

  let prescriptionStatusValues = Object.values(PatientEmailSendStatus);

  const prescriptionsSearchOptions = generateRadioButtonOptionsFromEnumAndPrefix(
      prescriptionStatusValues,
      "sendStatus",
      "enum.patientEmailSendStatus",
      getMessage);

  prescriptionsSearchOptions.forEach((prescriptionsSearchOptions, index) => {
    radioMap.set(prescriptionStatusValues[index], prescriptionsSearchOptions);
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await searchPatientEmails(initialSearchValues, cancelTokenSource)
        .then(response => {
          setPatientEmails(response)
          setPatientEmailsLoaded(true);
        })
        .catch((e) => {
          handleError(e.response.data)
        });
  };

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.invalidDocumentId,
      errorUtils.unknownPatientEmail
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          showErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        showErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        showErrorMessage(getMessage('error.general'));
      }
    }
  }

  const showErrorMessage = (errorMessage: string) => {
    //@ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      notification.addNotification({
        title: getMessage("error.header"),
        message: getMessage(errorMessage),
        level: 'error'
      });
    }
  };

  const successfullySentEmails = (sentEmails: number) => {

    //@ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      if (sentEmails > 0) {
        notification.addNotification({
          title: getMessage("patientEmails.successfullySendEmails.title"),
          message: getMessageWithNamedParams(
              "patientEmails.successfullySendEmails.message",
              {sentEmails: sentEmails}),
          level: 'success'
        });
      } else if (sentEmails == 0) {
        notification.addNotification({
          title: getMessage("error.header"),
          message: getMessage("error.patientEmail.sendPrescriptionFailed"),
          level: 'error'
        });
      }
    }
  };

  const sendSelectedPatientEmails = async () => {

    const request: SendPatientEmailsRequest = {
      patientEmailIds: Array.from(selectedPatientEmails)
    };

    try {
      const sentEmails = await sendPatientEmails(request, cancelTokenSource);
      filterPatientEmails();
      successfullySentEmails(sentEmails)
    } catch (e) {
      handleError(e.response.data);
    } finally {
    }
  };

  const filterPatientEmails = () => {
    setSelectedPatientEmails(new Set());
    fetchData();
  };

  const openPatientEmailPdf = async (
      id: number,
      documentId: number,
      treatmentId: number,
      documentType: string,
      sendStatus: PatientEmailSendStatus) => {

    const prescriptionDocumentRequest: PrescriptionDocumentRequest = {
      prescriptionId: documentId,
      treatmentId: treatmentId,
      treaterId: options.currentTreater!.id,
      mandateId: currentUser!.userConfig!.mandateId
    }

    setPatientEmailId(id);
    setDocumentId(documentId);
    setTreatmentId(treatmentId);

    let response;
    if (PatientDocumentType.PRESCRIPTION === documentType) {
      response =
          await getPrescriptionDocumentFromPendingEmailsList(prescriptionDocumentRequest, sendStatus, cancelTokenSource);
    } else {
      response = await getReportDocumentFromPendingEmailsList(documentId, sendStatus, cancelTokenSource);
    }

    await openPatientEmailPdfModal(response);
  }

  const cancelPatientEmailHandler = async (id: number) => {

    setPatientEmailsSaveInProgress(true);

    try {
      await cancelPatientEmail(id, cancelTokenSource);
      let patientEmails = await searchPatientEmails(searchValues, cancelTokenSource);
      setPatientEmails(patientEmails);
    } catch (e) {
      handleError(e.response.data);
    } finally {
      setPatientEmailsSaveInProgress(false);
    }

  }

  const openPatientEmailPdfModal = (
      prescriptionPdfResponse: PatientEmailFormDto[]) => {

    setDocumentOutput(prescriptionPdfResponse);
    setPatientEmailPdfModalOpen(true);
  };

  const closePrescriptionEmailPdfModal = () => {

    setPatientEmailPdfModalOpen(false);
    setDocumentOutput([]);
  };

  const savePatientEmail = async (emailRequest: UnableToWorkEmailRequest) => {

    setPatientEmailsSaveInProgress(true);

    const request: PatientEmailRequest = {
      mandateId: currentUser!.userConfig!.mandateId,
      documentId: documentId,
      documentType: PatientDocumentType.PRESCRIPTION,
      treatmentId: treatmentId,
      treaterId: options.currentTreater!.id,
      emailsTo: emailRequest.emailsTo,
      emailsCc: emailRequest.emailsCc,
      subject: emailRequest.subject,
      messageText: emailRequest.messageText
    }

    try {
      await editPatientEmail(patientEmailId, request, cancelTokenSource);
      filterPatientEmails();
    } catch (e) {
      handleError(e.response.data);
    } finally {
      setPatientEmailsSaveInProgress(false);
    }

    closePrescriptionEmailPdfModal();
  };

  const isPatientEmailSelectable = (patientEmail: PatientPrescriptionEmailDto) => {
    return patientEmail.id && patientEmail.emailsTo && patientEmail.sendStatus === PatientEmailSendStatus.REQUESTED;
  };

  const isPatientEmailSelected = (patientEmail: PatientPrescriptionEmailDto) => {
    return selectedPatientEmails.has(patientEmail.id);
  };

  const selectPatientEmail = (patientEmail: PatientPrescriptionEmailDto) => {

    if (isPatientEmailSelectable(patientEmail)) {

      let selected = new Set(selectedPatientEmails);

      const patientEmailId = patientEmail.id;
      if (selectedPatientEmails.has(patientEmailId)) {
        selected.delete(patientEmailId);
      } else {
        selected.add(patientEmailId);
      }

      setSelectedPatientEmails(selected);
    }
  };

  const handleChange = debounce(({values}: PrescriptionPendingEmailsFieldProps): void => {

    setPatientEmailsLoaded(false);

    let searchRequest: Partial<PatientEmailSearchRequest> = {
      ...values
    };

    if (values.prescriptionStatus === PatientEmailSendStatus.REQUESTED) {
      searchRequest.fromDate = undefined;
      searchRequest.toDate = undefined;
    }

    setSearchValues(searchRequest);

    searchPatientEmails(searchRequest, cancelTokenSource)
        .then(response => {
          setPatientEmails(response);
          setPatientEmailsLoaded(true);
        })
        .catch((e) => {
          handleError(e.response.data)
        })
        .finally(() => setPatientEmailsLoaded(true))
  }, 300);

  const renderPendingPrescriptionEmailsSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={initialSearchValues}
            subscription={{pristine: true, values: true}}
            render={renderSearchForm}
        />
    )
  }

  const renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const statusRequested = radioMap.get(PatientEmailSendStatus.REQUESTED);
    const statusSent = radioMap.get(PatientEmailSendStatus.SENT);
    const statusCanceled = radioMap.get(PatientEmailSendStatus.CANCELED);

    let showDateSearch = false;

    if (values && values.sendStatus === PatientEmailSendStatus.SENT) {
      showDateSearch = true;
    }

    return (
        <PxGrid className="search-segment">
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("patientEmails.searchSegment.searchStatus")}:
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="sendStatus"
                  component={Radio}
                  radioDefinition={statusRequested}
              />
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="sendStatus"
                  component={Radio}
                  radioDefinition={statusSent}
              />
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="sendStatus"
                  component={Radio}
                  radioDefinition={statusCanceled}
              />
            </Grid.Column>
          </Grid.Row>
          {showDateSearch &&
              <Grid.Row>
                <Grid.Column width={3}>
                  {getMessage("patientEmails.searchSegment.fromDate")}
                </Grid.Column>
                <Grid.Column width={2}>
                  <Field name="fromDate"
                         component={DatePicker}
                         fluid
                  />
                </Grid.Column>
                <Grid.Column width={1}>
                  {getMessage("patientEmails.searchSegment.toDate")}
                </Grid.Column>
                <Grid.Column width={2}>
                  <Field name="toDate"
                         component={DatePicker}
                         fluid
                  />
                </Grid.Column>
              </Grid.Row>
          }
          <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState)}/>
        </PxGrid>
    )
  }

  const renderPatientEmails = (): JSX.Element => {

    return (
        <VirtualizedTable
            rowCount={patientEmails.length}
            rowGetter={patientEmailsRowGetter}
            rowRenderer={patientEmailsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 60,
                label: selectHeaderColumn(),
                dataKey: "index",
                cellRenderer: selectablePrescriptionsSelectCellRenderer
              },
              {
                width: 200,
                label: getMessage("patientEmails.tableHeader.patientName"),
                dataKey: "patientFullName"
              },
              {
                width: 300,
                flexGrow: 1,
                label: getMessage("patientEmails.tableHeader.emailTo"),
                dataKey: "emailsTo",
                cellRenderer: emailCellRenderer
              },
              {
                width: 300,
                flexGrow: 1,
                label: getMessage("patientEmails.tableHeader.emailCc"),
                dataKey: "emailsCc",
                cellRenderer: emailCellRenderer
              },
              {
                width: 200,
                label: getMessage("patientEmails.tableHeader.emailSubject"),
                dataKey: "subject"
              },
              {
                width: 100,
                label: getMessage("patientEmails.tableHeader.rowActions"),
                dataKey: "prescriptionId",
                cellRenderer: rowActionCellRenderer
              }
            ]}
        />
    );
  };

  const patientEmailsRowGetter = ({index}: any) => {
    Object.assign(patientEmails[index], {index: index + 1});
    return patientEmails[index];
  }

  const patientEmailsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  const selectHeaderColumn = () => {

    const countSelectedPrescriptions = selectedPatientEmails.size;
    const countSelectablePrescriptions = patientEmails.filter(isPatientEmailSelectable).length;

    const indeterminate = countSelectedPrescriptions > 0
        && countSelectedPrescriptions < countSelectablePrescriptions;
    const noSelectedPrescriptions = countSelectedPrescriptions === 0
        && countSelectablePrescriptions > 0;
    const allPrescriptionsSelected = countSelectablePrescriptions > 0
        && countSelectedPrescriptions === countSelectablePrescriptions;

    return (
        <CheckboxSc
            checked={countSelectedPrescriptions > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedPrescriptions || indeterminate) {
                  const prescriptionIds = patientEmails
                      .filter(isPatientEmailSelectable)
                      .map(pr => pr.id);

                  setSelectedPatientEmails(new Set(prescriptionIds));
                }

                if (allPrescriptionsSelected) {
                  setSelectedPatientEmails(new Set());
                }

              }
            }
            disabled={countSelectablePrescriptions === 0}
        />
    );
  };

  const selectablePrescriptionsSelectCellRenderer = (data: any) => {

    const {rowData} = data;
    const prescription: PatientPrescriptionEmailDto = rowData;

    if (isPatientEmailSelectable(prescription)) {
      return (
          <CheckboxSc
              checked={isPatientEmailSelected(prescription)}
              onChange={() => selectPatientEmail(prescription)}
          />
      );
    }

    return <></>;
  };

  const emailCellRenderer = ({cellData}: any) => {
    const emails = cellData ? cellData.replace(";", ",") : '';

    return (
        <div>
          {emails}
        </div>
    );
  }

  const rowActionCellRenderer = (data: any) => {

    const {rowData} = data;
    const {id, documentId, treatmentId, documentType, sendStatus} = rowData;

    return (
        <div>
          <StyledButton icon
                        type="button"
                        onClick={() => openPatientEmailPdf(id, documentId, treatmentId, documentType, sendStatus)}
          >
            <Popup
                trigger={<Icon name="copy outline"/>}
                content={getMessage('patientEmails.tableRow.openPrescriptionButton')}
                size="small"
                position="top center"
                style={PopupStyle}
                inverted
            />
          </StyledButton>
          {
            sendStatus === "REQUESTED"
                ? <DeleteActionButton
                    hoverMessage={getMessage('patientEmails.tableRow.cancelEmailButton')}
                    popupMessage={getMessage("patientEmails.tableRow.confirmEmailCancel")}
                    trigger={<Icon name="window close"/>}
                    onConfirm={() => cancelPatientEmailHandler(id)}
                />
                : <></>}
        </div>
    )
  }

  const goToHome = () => {
    history.push("/");
  };

  const countSelectedEmails = selectedPatientEmails.size;
  const countSelectableEmails = patientEmails.filter(isPatientEmailSelectable).length;

  return (
      <ContentDiv>
        <FullScreenPageHeader headerMessage={getMessage("header.navigation.patientEmails")}
                              buttonMessage={getMessage("back")}
                              onConfirm={() => goToHome()}/>

        <StyledPraxSegment>
          {renderPendingPrescriptionEmailsSearchSegment()}

          <div style={{marginTop: "1rem", marginRight: "1rem"}}>
            <Button
                type="button"
                primary
                compact
                floated="right"
                disabled={countSelectedEmails === 0}
                onClick={() => sendSelectedPatientEmails()}
            >
              {getMessage("patientEmails.sendSelectedPrescriptions.button")}
            </Button>
          </div>

          <div style={{marginTop: "1rem", marginLeft: "1rem"}}>
            {getMessage("patientEmails.selectedEmails")}:&nbsp;
            <strong>
              <StyledCount>{countSelectedEmails}</StyledCount>/<StyledCount>{countSelectableEmails}</StyledCount>
            </strong>
          </div>

          <StyledTable style={{margin: "1rem !important"}}>
            {patientEmailsLoaded
                ? renderPatientEmails()
                : <LoaderComponent message={getMessage("loading")}/>
            }
          </StyledTable>
        </StyledPraxSegment>

        {patientEmailPdfModalOpen &&
            <UnableToWorkPdfModal
                modalOpen={patientEmailPdfModalOpen}
                onCloseModal={closePrescriptionEmailPdfModal}
                pdfResponses={documentOutput}
                getPdfUrl={getPdfUrl}
                showSendEmail={true}
                sendEmailTextKey="unableToWorkEmailForm.save"
                sendEmail={savePatientEmail}
                modalOpenFromPendingEmails={true}
                savingPrescription={patientEmailsSaveInProgress}
            />
        }
      </ContentDiv>
  );
}

export default withAuthContext(
    withPatientContext(
        withMessages(
            withNotificationSystemContext(PatientEmailsView))));
